import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarDays, Check, Clock, Loader2, Mail, Phone, User, ChevronLeft, ChevronRight, Syringe, Activity, Stethoscope, Leaf, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/site/Navbar";
import { Footer } from "../components/site/Footer";
import { api } from "@/lib/api";
import { APPOINTMENT_CATEGORIES } from "@/lib/categories";

const CATEGORY_ICONS = { Syringe, Activity, Stethoscope, Leaf, Sparkles };

const DAYS_FR = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const MONTHS_FR = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
const sameDate = (a, b) => a && b && ymd(a) === ymd(b);

const formatTime = (iso) => {
    const d = new Date(iso);
    return d.toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Zurich" });
};

const formatLongDate = (d) => d.toLocaleDateString("fr-CH", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "Europe/Zurich" });

export default function Booking() {
    const [step, setStep] = useState(1);
    const [types, setTypes] = useState([]);
    const [type, setType] = useState(null);
    const [month, setMonth] = useState(startOfMonth(new Date()));
    const [days, setDays] = useState([]); // [{date, slots, has_availability}]
    const [loadingMonth, setLoadingMonth] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [form, setForm] = useState({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        customer_birthdate: "",
        notes: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [confirmed, setConfirmed] = useState(null);

    // Load types
    useEffect(() => {
        api.get("/appointment-types").then((r) => setTypes(r.data || []));
    }, []);

    // Load availability for current month when type & month change
    useEffect(() => {
        if (!type) return;
        const from = startOfMonth(month);
        const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
        const today = new Date();
        const fromActual = (from < today && from.getMonth() === today.getMonth()) ? today : from;
        setLoadingMonth(true);
        api.get("/availability", {
            params: { type_id: type.id, from: ymd(fromActual), to: ymd(last) },
        }).then((r) => setDays(r.data || []))
          .catch(() => setDays([]))
          .finally(() => setLoadingMonth(false));
    }, [type, month]);

    const selectedDay = useMemo(
        () => days.find((d) => d.date === ymd(selectedDate || new Date(0))),
        [days, selectedDate]
    );

    const calendarCells = useMemo(() => {
        const first = startOfMonth(month);
        const startWeekday = (first.getDay() + 6) % 7; // Mon=0
        const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
        const cells = [];
        for (let i = 0; i < startWeekday; i++) cells.push(null);
        for (let i = 1; i <= daysInMonth; i++) cells.push(new Date(month.getFullYear(), month.getMonth(), i));
        return cells;
    }, [month]);

    const dayMap = useMemo(() => {
        const m = {};
        for (const d of days) m[d.date] = d;
        return m;
    }, [days]);

    const submit = async () => {
        if (submitting) return;
        if (!form.customer_name || !form.customer_email) {
            toast.error("Veuillez remplir nom et email");
            return;
        }
        if (type.requires_birthdate && !form.customer_birthdate) {
            toast.error("Date de naissance requise pour ce type de rendez-vous");
            return;
        }
        setSubmitting(true);
        try {
            const r = await api.post("/appointments", {
                type_id: type.id,
                start_at: selectedSlot.start_at,
                ...form,
                customer_birthdate: form.customer_birthdate || null,
            });
            setConfirmed(r.data);
            setStep(4);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Échec de la réservation");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-brand-cream" data-testid="booking-page">
            <Navbar />
            <main className="pt-32 pb-24">
                <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
                    <Link to="/" className="inline-flex items-center gap-2 text-sm text-brand-primary mb-8 hover:underline underline-offset-4">
                        <ArrowLeft className="h-4 w-4" /> Retour à l'accueil
                    </Link>

                    <div className="mb-12">
                        <p className="ll-eyebrow text-brand-primary mb-4">Prendre rendez-vous</p>
                        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-brand-ink">
                            Réservez en ligne, <span className="italic text-brand-primary">en moins de 2 minutes</span>.
                        </h1>
                        <p className="mt-5 text-lg text-brand-ink/70 font-sans max-w-2xl">
                            Choisissez le type de rendez-vous, sélectionnez un créneau qui vous arrange — nous vous attendons en pharmacie.
                        </p>
                    </div>

                    {/* Stepper */}
                    <div className="flex items-center gap-2 mb-12 text-xs font-sans">
                        {["Type", "Créneau", "Coordonnées", "Confirmation"].map((label, i) => {
                            const n = i + 1;
                            const active = step === n;
                            const done = step > n;
                            return (
                                <React.Fragment key={label}>
                                    <div className={`flex items-center gap-2 ${active ? "text-brand-primary" : done ? "text-brand-ink/60" : "text-brand-ink/30"}`}>
                                        <span className={`h-7 w-7 inline-flex items-center justify-center rounded-full ${active ? "bg-brand-primary text-brand-cream" : done ? "bg-brand-mint text-brand-primary" : "bg-brand-line"}`}>
                                            {done ? <Check className="h-3.5 w-3.5" /> : n}
                                        </span>
                                        <span className="hidden sm:inline">{label}</span>
                                    </div>
                                    {n < 4 && <span className="flex-1 h-px bg-brand-line" />}
                                </React.Fragment>
                            );
                        })}
                    </div>

                    {/* Step 1: choose type */}
                    {step === 1 && (
                        <div data-testid="booking-step-type">
                            <h2 className="font-display text-2xl mb-6 text-brand-ink">Quel type de rendez-vous ?</h2>

                            {(() => {
                                // Group types by category, preserving the explicit order
                                const grouped = APPOINTMENT_CATEGORIES.map((cat) => ({
                                    ...cat,
                                    items: types.filter((t) => (t.category || "") === cat.key),
                                })).filter((g) => g.items.length > 0);

                                const uncategorized = types.filter((t) => !t.category);
                                if (uncategorized.length) {
                                    grouped.push({
                                        key: "_uncategorized",
                                        label: "Autres rendez-vous",
                                        icon: "Sparkles",
                                        items: uncategorized,
                                    });
                                }

                                return grouped.map((cat) => {
                                    const Icon = CATEGORY_ICONS[cat.icon] || Sparkles;
                                    return (
                                        <section key={cat.key} className="mb-12" data-testid={`booking-category-${cat.key}`}>
                                            <div className="flex items-center gap-3 mb-5 pb-3 border-b border-brand-line">
                                                <span className="h-9 w-9 rounded-full bg-brand-mint/40 text-brand-primary inline-flex items-center justify-center">
                                                    <Icon className="h-4 w-4" />
                                                </span>
                                                <h3 className="font-display text-xl lg:text-2xl text-brand-ink">{cat.label}</h3>
                                                <span className="ll-eyebrow text-brand-ink/40">
                                                    {cat.items.length} prestation{cat.items.length > 1 ? "s" : ""}
                                                </span>
                                            </div>
                                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {cat.items.map((t) => (
                                                    <button
                                                        key={t.id}
                                                        onClick={() => { setType(t); setStep(2); }}
                                                        className="text-left bg-white border border-brand-line p-6 hover:border-brand-primary/60 hover:shadow-md transition-all duration-200 group"
                                                        data-testid={`type-card-${t.id}`}
                                                    >
                                                        <div className="flex items-center justify-between mb-4">
                                                            <span className="h-10 w-10 rounded-full flex items-center justify-center" style={{background: t.color + "20", color: t.color}}>
                                                                <CalendarDays className="h-4 w-4" />
                                                            </span>
                                                            <span className="ll-eyebrow text-brand-ink/40 inline-flex items-center gap-1">
                                                                <Clock className="h-3 w-3" />{t.duration_minutes} min
                                                            </span>
                                                        </div>
                                                        <h4 className="font-display text-lg text-brand-ink mb-2">{t.name}</h4>
                                                        {t.description && (
                                                            <p className="font-sans text-sm text-brand-ink/65 leading-relaxed">{t.description}</p>
                                                        )}
                                                        <span className="mt-4 inline-flex items-center gap-1 text-sm text-brand-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                                                            Choisir <ArrowRight className="h-3.5 w-3.5" />
                                                        </span>
                                                    </button>
                                                ))}
                                            </div>
                                        </section>
                                    );
                                });
                            })()}
                        </div>
                    )}

                    {/* Step 2: calendar + slots */}
                    {step === 2 && type && (
                        <div data-testid="booking-step-calendar">
                            <button onClick={() => setStep(1)} className="text-sm text-brand-ink/60 inline-flex items-center gap-1 mb-6 hover:text-brand-primary">
                                <ArrowLeft className="h-3.5 w-3.5" /> Changer de type
                            </button>
                            <h2 className="font-display text-2xl mb-2 text-brand-ink">{type.name}</h2>
                            <p className="font-sans text-sm text-brand-ink/60 mb-8">Durée : {type.duration_minutes} min — choisissez un créneau</p>

                            <div className="grid lg:grid-cols-[1.2fr_1fr] gap-8">
                                {/* Calendar */}
                                <div className="bg-white border border-brand-line p-6">
                                    <div className="flex items-center justify-between mb-5">
                                        <button
                                            onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
                                            disabled={month <= startOfMonth(new Date())}
                                            className="h-9 w-9 rounded-full border border-brand-line flex items-center justify-center disabled:opacity-30 hover:bg-brand-mint/30"
                                            data-testid="cal-prev"
                                        >
                                            <ChevronLeft className="h-4 w-4" />
                                        </button>
                                        <p className="font-display text-xl text-brand-ink">
                                            {MONTHS_FR[month.getMonth()]} {month.getFullYear()}
                                        </p>
                                        <button
                                            onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
                                            className="h-9 w-9 rounded-full border border-brand-line flex items-center justify-center hover:bg-brand-mint/30"
                                            data-testid="cal-next"
                                        >
                                            <ChevronRight className="h-4 w-4" />
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-7 gap-1 text-center text-xs font-sans text-brand-ink/50 mb-2">
                                        {DAYS_FR.map((d) => <div key={d}>{d}</div>)}
                                    </div>

                                    {loadingMonth ? (
                                        <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand-primary" /></div>
                                    ) : (
                                        <div className="grid grid-cols-7 gap-1">
                                            {calendarCells.map((d, i) => {
                                                if (!d) return <div key={`empty-${i}`} />;
                                                const info = dayMap[ymd(d)];
                                                const past = d < new Date(new Date().toDateString());
                                                const has = info && info.has_availability;
                                                const isSelected = sameDate(selectedDate, d);
                                                return (
                                                    <button
                                                        key={ymd(d)}
                                                        disabled={!has || past}
                                                        onClick={() => { setSelectedDate(d); setSelectedSlot(null); }}
                                                        className={`aspect-square rounded-sm font-sans text-sm transition-all
                                                            ${isSelected ? "bg-brand-primary text-brand-cream" :
                                                              has && !past ? "bg-brand-mint/40 text-brand-ink hover:bg-brand-mint" :
                                                              "text-brand-ink/30 line-through cursor-not-allowed"}`}
                                                        data-testid={`cal-day-${ymd(d)}`}
                                                        title={!has ? "Complet" : ""}
                                                    >
                                                        {d.getDate()}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}

                                    <div className="mt-6 flex flex-wrap gap-4 text-xs font-sans">
                                        <span className="inline-flex items-center gap-2"><span className="h-3 w-3 rounded-sm bg-brand-mint/60" /> Disponible</span>
                                        <span className="inline-flex items-center gap-2"><span className="h-3 w-3 rounded-sm bg-brand-primary" /> Sélectionné</span>
                                        <span className="inline-flex items-center gap-2 text-brand-ink/50"><span className="h-3 w-3 rounded-sm bg-brand-line" /> Complet</span>
                                    </div>
                                </div>

                                {/* Slots */}
                                <div className="bg-white border border-brand-line p-6 max-h-[520px] overflow-y-auto">
                                    {!selectedDate ? (
                                        <div className="text-center py-16 text-brand-ink/50 font-sans">
                                            <CalendarDays className="h-10 w-10 mx-auto mb-4 opacity-40" />
                                            Sélectionnez un jour pour voir les créneaux
                                        </div>
                                    ) : (
                                        <>
                                            <p className="ll-eyebrow text-brand-primary mb-1">Créneaux du</p>
                                            <p className="font-display text-xl text-brand-ink mb-5 capitalize">
                                                {formatLongDate(selectedDate)}
                                            </p>
                                            {selectedDay && selectedDay.slots.length > 0 ? (
                                                <div className="grid grid-cols-3 gap-2">
                                                    {selectedDay.slots.map((s) => {
                                                        const sel = selectedSlot && selectedSlot.start_at === s.start_at;
                                                        return (
                                                            <button
                                                                key={s.start_at}
                                                                onClick={() => setSelectedSlot(s)}
                                                                className={`py-2.5 px-1 text-sm font-sans border transition-all
                                                                    ${sel ? "bg-brand-primary text-brand-cream border-brand-primary" :
                                                                            "border-brand-line hover:border-brand-primary text-brand-ink"}`}
                                                                data-testid={`slot-${s.start_at}`}
                                                            >
                                                                {formatTime(s.start_at)}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <p className="text-brand-ink/50 font-sans text-sm">Aucun créneau ce jour-là.</p>
                                            )}

                                            {selectedSlot && (
                                                <button
                                                    onClick={() => setStep(3)}
                                                    className="ll-btn ll-btn-primary w-full mt-8 justify-center"
                                                    data-testid="confirm-slot-btn"
                                                >
                                                    Continuer <ArrowRight className="h-4 w-4" />
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 3: form */}
                    {step === 3 && type && selectedSlot && (
                        <div data-testid="booking-step-form" className="max-w-2xl">
                            <button onClick={() => setStep(2)} className="text-sm text-brand-ink/60 inline-flex items-center gap-1 mb-6 hover:text-brand-primary">
                                <ArrowLeft className="h-3.5 w-3.5" /> Changer de créneau
                            </button>

                            <div className="bg-brand-mint/30 border border-brand-mint p-5 mb-8 flex items-start gap-4">
                                <CalendarDays className="h-5 w-5 text-brand-primary mt-1 shrink-0" />
                                <div className="text-sm font-sans">
                                    <p className="font-display text-base text-brand-ink mb-1">{type.name}</p>
                                    <p className="text-brand-ink/70 capitalize">{formatLongDate(selectedDate)} — {formatTime(selectedSlot.start_at)} ({type.duration_minutes} min)</p>
                                </div>
                            </div>

                            <h2 className="font-display text-2xl mb-6 text-brand-ink">Vos coordonnées</h2>
                            <div className="bg-white border border-brand-line p-8 space-y-5 grain">
                                <FieldRow icon={<User className="h-4 w-4" />} label="Nom complet *">
                                    <input className="w-full bg-transparent border-b border-brand-ink/30 focus:border-brand-primary outline-none py-2 font-sans"
                                        value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                                        data-testid="booking-name-input" />
                                </FieldRow>
                                <FieldRow icon={<Mail className="h-4 w-4" />} label="Courriel *">
                                    <input type="email" className="w-full bg-transparent border-b border-brand-ink/30 focus:border-brand-primary outline-none py-2 font-sans"
                                        value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                                        data-testid="booking-email-input" />
                                </FieldRow>
                                <FieldRow icon={<Phone className="h-4 w-4" />} label="Téléphone">
                                    <input className="w-full bg-transparent border-b border-brand-ink/30 focus:border-brand-primary outline-none py-2 font-sans"
                                        value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                                        data-testid="booking-phone-input" />
                                </FieldRow>
                                {type.requires_birthdate && (
                                    <FieldRow icon={<CalendarDays className="h-4 w-4" />} label="Date de naissance *">
                                        <input type="date" className="w-full bg-transparent border-b border-brand-ink/30 focus:border-brand-primary outline-none py-2 font-sans"
                                            value={form.customer_birthdate} onChange={(e) => setForm({ ...form, customer_birthdate: e.target.value })}
                                            data-testid="booking-birthdate-input" />
                                    </FieldRow>
                                )}
                                <FieldRow label="Précisions (optionnel)">
                                    <p className="font-sans text-xs text-brand-ink/55 mb-1" data-testid="booking-notes-warning">
                                        Merci de ne pas indiquer d'informations médicales confidentielles (diagnostic, traitement…) :
                                        vous pourrez en parler directement avec le pharmacien lors du rendez-vous.
                                    </p>
                                    <textarea rows={3} className="w-full bg-transparent border-b border-brand-ink/30 focus:border-brand-primary outline-none py-2 font-sans resize-none"
                                        value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                        data-testid="booking-notes-input" />
                                </FieldRow>

                                <p className="text-xs text-brand-ink/55 font-sans leading-relaxed" data-testid="booking-privacy-notice">
                                    Vos données servent uniquement à organiser votre rendez-vous, sont accessibles à la seule équipe
                                    de la pharmacie et supprimées 12 mois après le rendez-vous.{" "}
                                    <Link to="/confidentialite" className="underline underline-offset-2 text-brand-primary">Politique de confidentialité</Link>
                                </p>

                                <button onClick={submit} disabled={submitting} className="ll-btn ll-btn-primary w-full justify-center mt-4 disabled:opacity-60"
                                    data-testid="booking-submit-btn">
                                    {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Réservation…</> : <>Confirmer le rendez-vous <ArrowRight className="h-4 w-4" /></>}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 4: confirmation */}
                    {step === 4 && confirmed && (
                        <div className="max-w-2xl text-center mx-auto" data-testid="booking-step-confirmed">
                            <div className="h-16 w-16 rounded-full bg-emerald-100 text-emerald-600 inline-flex items-center justify-center mb-8">
                                <Check className="h-8 w-8" />
                            </div>
                            <h2 className="font-display text-3xl md:text-4xl text-brand-ink mb-4">
                                Rendez-vous confirmé
                            </h2>
                            <p className="font-sans text-brand-ink/70 mb-8">
                                Merci {confirmed.customer_name.split(" ")[0]} ! Votre rendez-vous est enregistré.
                            </p>
                            <div className="bg-white border border-brand-line p-8 text-left space-y-3">
                                <Row label="Type" value={confirmed.type_name} />
                                <Row label="Date" value={formatLongDate(new Date(confirmed.start_at))} />
                                <Row label="Heure" value={`${formatTime(confirmed.start_at)} (${confirmed.duration_minutes} min)`} />
                                <Row label="Pharmacien·ne" value={confirmed.pharmacist_name} />
                                <Row label="Lieu" value="Pharmacie La Lutrive — Lutry" />
                            </div>
                            <p className="mt-8 text-sm text-brand-ink/60 font-sans">
                                Une confirmation par email vous sera envoyée prochainement. En cas d'empêchement, contactez-nous au 021 791 53 53.
                            </p>
                            <Link to="/" className="ll-btn ll-btn-ghost mt-8">
                                Retour à l'accueil
                            </Link>
                        </div>
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
}

const FieldRow = ({ icon, label, children }) => (
    <div>
        <label className="ll-eyebrow text-brand-ink/60 mb-2 inline-flex items-center gap-2">
            {icon}
            {label}
        </label>
        {children}
    </div>
);

const Row = ({ label, value }) => (
    <div className="flex justify-between gap-4 py-2 border-b last:border-b-0 border-brand-line">
        <span className="font-sans text-sm text-brand-ink/55">{label}</span>
        <span className="font-sans text-sm text-brand-ink text-right">{value}</span>
    </div>
);
